import BoardPage from './pages/BoardPage';
import SprintPage from './pages/SprintPage';
import LandingPage from './pages/LandingPage';
import CardPage from "./pages/CardPage";
import ProjectPage from './pages/ProjectPage';
import RulesPage from './pages/RulesPage';
import { startDueDateCheck } from './utils/scheduledTasks';
import ProtectedRoute from './Components/auth/ProtectedRoute';
import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import UserList from './pages/UserList';
import Legal from "./pages/Legal";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import UserGroups from './pages/UserGroups';
import AddBoard from './pages/AddBoard';
import AddBoardAccess from './pages/AddBoardAccess';
import BoardAccess from './pages/BoardAccess';
import AddUserGroup from './pages/AddUserGroup';
import MainLayout from './Components/UserManagementComponents/MainLayout';
import MyAccount from './pages/MyAccount';
import { TenantProvider } from './Components/context/TenantContext';
import { auth } from './Components/auth/firebaseConfig';
import { SearchProvider } from './Components/context/SearchContext';
import DashboardPage from './pages/DashboardPage';
import ReportsPage from './pages/ReportPage';
import TenantPage from './pages/TenantPage';
import TenantPricingConsole from './pages/TenantPricingConsole';
import MilestonePage from './pages/MilestonePage';
import { isSuperAdminEmail } from './utils/superAdminUtils';
import { buildCcsPricingUrl } from './utils/ccsNavigation';

function CcsPricingRedirect() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    window.location.replace(buildCcsPricingUrl({
      tenantId: params.get('tenantId') || '',
      source: params.get('source') || undefined,
    }));
  }, []);

  return null;
}

function App() {
  const dueDateCheckStartedRef = useRef(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      console.log('Auth state changed:', user);
      if (!user) {
        dueDateCheckStartedRef.current = false;
        return;
      }

      try {
        const isSuperadmin = isSuperAdminEmail(user?.email);
        if (isSuperadmin && !dueDateCheckStartedRef.current) {
          startDueDateCheck();
          dueDateCheckStartedRef.current = true;
        }
      } catch (error) {
        console.error('Unable to verify superadmin access for due-date check:', error);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <TenantProvider>
      <SearchProvider>
        <Router>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <CssBaseline />
            <Routes>
              <Route path='/' element={<LandingPage />} />

              <Route
                path='/card-page/:boardId'
                element={
                  <ProtectedRoute
                    element={<CardPage />}
                    roles={['user', 'Admin']}
                  />
                }
              />

              <Route
                path='/board-page/:projectId'
                element={
                  <ProtectedRoute
                    element={<BoardPage />}
                    roles={['user', 'Admin']}
                  />
                }
              />
              <Route
                path='/sprints/:projectId'
                element={
                  <ProtectedRoute
                    element={<SprintPage />}
                    roles={['user', 'Admin']}
                  />
                }
              />
              <Route
                path='/milestone/:projectId'
                element={
                  <ProtectedRoute
                    element={<MilestonePage />}
                    roles={['user', 'Admin']}
                  />
                }
              />
              <Route
                path='/project-page'
                element={
                  <ProtectedRoute
                    element={<ProjectPage />}
                    roles={['user', 'Admin']}
                  />
                }
              />

              <Route element={<MainLayout />}>
                <Route path="/" element={<Navigate to="/user-list" replace />} />
                <Route
                  path='/user-list'
                  element={
                    <ProtectedRoute
                      element={<UserList />}
                      roles={['Admin']}
                    />
                  }
                />

                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute
                      element={<ReportsPage />}
                      roles={['user', 'Admin']}
                    />
                  }
                />

                <Route path="/user-groups" element={<ProtectedRoute element={<UserGroups />} roles={['Admin']} />} />
                <Route path="/add-user-group" element={<ProtectedRoute element={<AddUserGroup />} roles={['Admin']} />} />
                <Route path="/boards" element={<ProtectedRoute roles={['Admin']} />} />
                <Route path="/add-board" element={<ProtectedRoute element={<AddBoard />} roles={['Admin']} />} />
                <Route path="/board-access" element={<ProtectedRoute element={<BoardAccess />} roles={['Admin']} />} />
                <Route path="/my-account" element={<ProtectedRoute element={<MyAccount />} roles={['Admin']} />} />
                <Route path="/add-board-access" element={<ProtectedRoute element={<AddBoardAccess />} roles={['Admin']} />} />
              </Route>
              <Route
                path='/rules'
                element={
                  <ProtectedRoute
                    element={<RulesPage />}
                    roles={['Admin', 'SuperAdmin']}
                  />
                }
              />
              <Route
                path='/dashboard/:projectId'
                element={
                  <ProtectedRoute
                    element={<DashboardPage />}
                    roles={['user', 'Admin']}
                  />
                }
              />
              <Route path="/legal" element={<Legal />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/pricing" element={<CcsPricingRedirect />} />
              <Route path="/checkout" element={<CcsPricingRedirect />} />
              <Route
                path='/tenant-page'
                element={
                  <ProtectedRoute
                    element={<TenantPage />}
                    roles={['SuperAdmin']}
                  />
                }
              />
              <Route
                path='/tenant-pricing-console'
                element={
                  <ProtectedRoute
                    element={<TenantPricingConsole />}
                    roles={['SuperAdmin']}
                  />
                }
              />
            </Routes>
          </Box>
        </Router>
      </SearchProvider>
    </TenantProvider>
  );
}

export default App;
